import React, { useState } from "react";
import { useColors } from "../constants/Colors";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useProfile } from "../contexts/ProfileContext";
import AuthModal from "../components/AuthModal";
import ShareModal from "../components/ShareModal";
import AddMembersModal from "../components/AddMembersModal";
import TabSwitcher from "../components/TabSwitcher";
import PageLayout from "../components/PageLayout";
import EmptyState from "../components/EmptyState";
import FlatDetailCard from "../components/FlatDetailCard";
import type { FlatCardData, FlatLocationData } from "../components/FlatDetailCard";
import { Link, useNavigate } from "react-router-dom";
import { trpcReact } from "../trpc_client";


const FlatsPlaceholder: React.FC = () => {
  const { t } = useTranslation();
  const colors = useColors();
  const { user } = useAuth();
  const { userExistsInDb } = useProfile();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [activeTab, setActiveTab] = useState<"myFlats" | "saved">("myFlats");
  const [shareModalOpen, setShareModalOpen] = useState(false);
  type ShareModalFlat = React.ComponentProps<typeof ShareModal>["flat"];
  type ShareModalRoom = NonNullable<ShareModalFlat["room"]>[number];
  const [flatToShare, setFlatToShare] = useState<
    React.ComponentProps<typeof ShareModal>["flat"] | null
  >(null);
  const [addMembersModalOpen, setAddMembersModalOpen] = useState(false);
  const [flatForAddMembers, setFlatForAddMembers] = useState<{
    flat_id: number;
    name: string;
  } | null>(null);

  // Fetch user's flats
  const { data: userFlats, isLoading: userFlatsLoading } =
    trpcReact.getUserFlats.useQuery(undefined, {
      enabled: userExistsInDb,
    });

  // Fetch saved flats
  const {
    data: savedFlats,
    isLoading: savedFlatsLoading,
    refetch: refetchSavedFlats,
  } = trpcReact.getSavedFlats.useQuery(undefined, {
    enabled: userExistsInDb,
  });

  // Unsave flat mutation
  const unSaveFlatMutation = trpcReact.unSaveFlat.useMutation({
    onSuccess: () => refetchSavedFlats(),
  });

  const isLoading = userFlatsLoading || savedFlatsLoading;

  // Show sign-in prompt if not authenticated
  if (!user) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center"
        style={{ backgroundColor: colors.containerNeutralSolidSurface }}
      >
        <EmptyState
          type="no-flats"
          title={t("flats_tab.tabbar_title") || "My Flats"}
          description="Sign in to manage your flats or create a new listing."
          action={{
            label: t("sign_in") || "Sign in",
            onClick: () => setShowAuth(true),
          }}
        />
        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          onSuccess={() => setShowAuth(false)}
        />
      </div>
    );
  }

  // Helper to prepare share modal data from a flat
  const prepareShareFlat = (flat: FlatCardData) => {
    let locationData;
    try {
      locationData = flat.location
        ? (JSON.parse(flat.location) as FlatLocationData)
        : {};
    } catch {
      locationData = {};
    }
    setFlatToShare({
      flat_id: flat.flat_id,
      name: flat.name,
      location: locationData,
      member_count: flat.member_count,
      size:
        typeof flat.size === "number"
          ? flat.size
          : Number(flat.size || 0) || undefined,
      room: (flat.room || []).map(
        (flatRoom): ShareModalRoom => ({
          rent:
            typeof flatRoom.rent === "number"
              ? flatRoom.rent
              : Number(flatRoom.rent || 0) || undefined,
          utilities:
            typeof flatRoom.utilities === "number"
              ? flatRoom.utilities
              : Number(flatRoom.utilities || 0) || undefined,
          deposit:
            typeof flatRoom.deposit === "number"
              ? flatRoom.deposit
              : Number(flatRoom.deposit || 0) || undefined,
          size:
            typeof flatRoom.size === "number"
              ? flatRoom.size
              : Number(flatRoom.size || 0) || undefined,
          furnishing: flatRoom.furnishing,
          permanent: flatRoom.permanent,
          from_date: flatRoom.from_date || undefined,
          to_date: flatRoom.to_date || undefined,
        }),
      ),
      flat_text:
        (typeof flat.flat_text === "object" && flat.flat_text
          ? flat.flat_text.description
          : flat.flat_text) || "",
    });
    setShareModalOpen(true);
  };

  // SVG icon helpers for action buttons
  const ShareIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" /><polyline points="16 6 12 2 8 6" /><line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
  const AddMembersIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="8.5" cy="7" r="4" /><line x1="20" y1="8" x2="20" y2="14" /><line x1="23" y1="11" x2="17" y2="11" />
    </svg>
  );
  const EditIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
  const UnsaveIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );

  return (
    <PageLayout
      title={t("flats_tab.tabbar_title") || "Flats"}
      headerButton={
        <Link
          to="/create-flat"
          className="rounded-full px-4 py-2 text-sm font-gnarly-semibold transition-all duration-150 hover:scale-[1.03] active:scale-[0.97] flex items-center gap-1.5"
          style={{
            backgroundColor: colors.accentColor,
            color: colors.textNeutralOnPrimary,
          }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          {t("create_flat") || "Create Flat"}
        </Link>
      }
      headerContent={
        <TabSwitcher
          tabs={[
            { id: "myFlats", label: "My Flats", count: userFlats?.length || 0 },
            { id: "saved", label: "Saved", count: savedFlats?.length || 0 },
          ]}
          activeTab={activeTab}
          onTabChange={(tabId) => setActiveTab(tabId as "myFlats" | "saved")}
        />
      }
    >
      {isLoading && (
        <div
          className="text-center py-12"
          style={{ color: colors.textNeutralMuted }}
        >
          Loading flats...
        </div>
      )}

      {/* My Flats Tab */}
      {!isLoading && activeTab === "myFlats" && (
        <>
          {(!userFlats || userFlats.length === 0) && (
            <EmptyState
              type="no-flats"
              action={{
                label: "Create your first flat",
                onClick: () => navigate("/create-flat"),
              }}
            />
          )}

          {userFlats && userFlats.length > 0 && (
            <div className="flex flex-col gap-6 mt-6 w-full min-w-0">
              {userFlats.map((item) => {
                const flat = item.flat;
                const images = item.urls || [];
                const room = flat.room?.[0];
                const isPrivate = !flat.public;

                return (
                  <FlatDetailCard
                    key={flat.flat_id}
                    flat={flat}
                    images={images}
                    room={room}
                    isPrivate={isPrivate}
                    statusBadge={
                      <div
                        className="px-2.5 py-1 rounded-full text-[11px] font-gnarly-semibold"
                        style={{
                          backgroundColor: isPrivate
                            ? "rgba(2, 2, 16, 0.06)"
                            : `${colors.accentColor}14`,
                          color: isPrivate
                            ? colors.textNeutralMuted
                            : colors.accentColor,
                        }}
                      >
                        {isPrivate
                          ? t("flats_tab.slider.left.private") || "Private"
                          : t("public") || "Public"}
                      </div>
                    }
                    actions={[
                      {
                        label: t("add_members") || "Add Members",
                        icon: <AddMembersIcon />,
                        primary: true,
                        onClick: (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFlatForAddMembers({ flat_id: flat.flat_id, name: flat.name });
                          setAddMembersModalOpen(true);
                        },
                      },
                      {
                        label: t("share") || "Share",
                        icon: <ShareIcon />,
                        onClick: (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          prepareShareFlat(flat);
                        },
                      },
                      {
                        label: t("edit") || "Edit",
                        icon: <EditIcon />,
                        onClick: (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          navigate(`/app/edit-flat/${flat.flat_id}`);
                        },
                      },
                    ]}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Saved Flats Tab */}
      {!isLoading && activeTab === "saved" && (
        <>
          {(!savedFlats || savedFlats.length === 0) && (
            <EmptyState
              type="no-saved"
              action={{
                label: "Browse flats",
                onClick: () => navigate("/"),
              }}
            />
          )}

          {savedFlats && savedFlats.length > 0 && (
            <div className="flex flex-col gap-6 mt-6 w-full min-w-0">
              {savedFlats.map((item) => {
                const flat = item.flat;
                const images = item.urls || [];
                const room = flat?.room?.[0];

                if (!flat) return null;

                return (
                  <FlatDetailCard
                    key={flat.flat_id}
                    flat={flat}
                    images={images}
                    room={room}
                    actions={[
                      {
                        label: t("share") || "Share",
                        icon: <ShareIcon />,
                        onClick: (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          prepareShareFlat(flat);
                        },
                      },
                      {
                        label: t("unsave") || "Unsave",
                        icon: <UnsaveIcon />,
                        onClick: (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (room?.room_id) {
                            unSaveFlatMutation.mutate({ room_id: room.room_id });
                          }
                        },
                      },
                    ]}
                  />
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Share Modal */}
      {flatToShare && (
        <ShareModal
          isOpen={shareModalOpen}
          onClose={() => {
            setShareModalOpen(false);
            setFlatToShare(null);
          }}
          flat={flatToShare}
        />
      )}

      {/* Add Members Modal */}
      {flatForAddMembers && (
        <AddMembersModal
          isOpen={addMembersModalOpen}
          onClose={() => {
            setAddMembersModalOpen(false);
            setFlatForAddMembers(null);
          }}
          flatId={flatForAddMembers.flat_id}
          flatName={flatForAddMembers.name}
        />
      )}
    </PageLayout>
  );
};

export default FlatsPlaceholder;
